import { Button } from "@devhop/ui";
import {
  CopyIcon,
  PlusIcon,
  TrashIcon,
  UserIcon,
  UserPlusIcon,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { config } from "@/shared/lib/config";
import { hydrateLookupItems } from "@/shared/lib/utils";
import type {
  AssignmentDTO,
  CreateAssignmentDTO,
  EventKind,
  EventShowDTO,
  RoleFeeKind,
} from "../../domain/contracts";
import { resolvePlannedLineKey } from "../../domain/services/assignment-bulk-ui";
import {
  isMusicArrangerShowBaseAmount,
  MUSIC_ARRANGER_SHOW_BASE_AMOUNTS,
} from "../../domain/contracts";
import { AssignmentForm, type AssignmentRulePayload } from "./AssignmentForm";
import { PerformerMultiSelect } from "./PerformerMultiSelect";
import { QuickCreatePerformerPanel } from "./QuickCreatePerformerPanel";

type RuleLine = { id: string; payload: AssignmentRulePayload | null };

export type PerformerAssignmentBuilderChange = {
  assignments: CreateAssignmentDTO[];
  performerNames: Map<string, string>;
};

type PerformerAssignmentBuilderProps = {
  eventKind: EventKind;
  derivedShowCount?: number;
  eventShows?: EventShowDTO[];
  existingAssignments: AssignmentDTO[];
  submitting?: boolean;
  onChange: (data: PerformerAssignmentBuilderChange) => void;
};

function newLine(payload: AssignmentRulePayload | null = null): RuleLine {
  return { id: crypto.randomUUID(), payload };
}

function defaultLinePayload(
  roleFeeKind: RoleFeeKind,
  showCount = 1,
  eventShows?: EventShowDTO[],
): AssignmentRulePayload {
  const allShowIds =
    eventShows && eventShows.length >= 2
      ? eventShows.map((show) => show.id)
      : undefined;
  return {
    roleFeeKind,
    showCount: roleFeeKind === "music_arranger" ? 1 : showCount,
    eventShowIds:
      roleFeeKind === "music_arranger" && allShowIds
        ? [allShowIds[0]!]
        : allShowIds,
    rehearsalDays: 0,
    showBaseAmountOverride:
      roleFeeKind === "music_arranger"
        ? MUSIC_ARRANGER_SHOW_BASE_AMOUNTS[0]
        : undefined,
    lineKey: "default",
    notes: null,
  };
}

function performerLookupUrl(eventKind: EventKind) {
  if (eventKind === "kol") {
    return `${config.apiUrl}/performers/lookup?performerType=kol`;
  }
  return `${config.apiUrl}/performers/lookup`;
}

function payloadToInitial(payload: AssignmentRulePayload) {
  return {
    roleFeeKind: payload.roleFeeKind,
    showCount: payload.showCount,
    eventShowIds: payload.eventShowIds,
    rehearsalDays: payload.rehearsalDays,
    showBaseAmountOverride: payload.showBaseAmountOverride,
    lineKey: payload.lineKey ?? "default",
    notes: payload.notes ?? null,
  };
}

export function PerformerAssignmentBuilder({
  eventKind,
  derivedShowCount,
  eventShows,
  existingAssignments,
  submitting,
  onChange,
}: PerformerAssignmentBuilderProps) {
  const resolvedShowCount = Math.max(1, derivedShowCount ?? 1);
  const canCreatePerformer = useActionPermission(["performers:create"]);
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);
  const [performerIds, setPerformerIds] = useState<string[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [linesByPerformer, setLinesByPerformer] = useState<
    Map<string, RuleLine[]>
  >(new Map());
  const [names, setNames] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    if (performerIds.length === 0) {
      setLinesByPerformer(new Map());
      setNames(new Map());
      setSelectedId(null);
      return;
    }

    let cancelled = false;
    void hydrateLookupItems<{
      id: string;
      name: string;
      defaultRoleFeeKind?: RoleFeeKind;
    }>(performerLookupUrl(eventKind), performerIds).then((items) => {
      if (cancelled) return;
      const nameMap = new Map<string, string>();
      const defaultRoleById = new Map<string, RoleFeeKind>();
      for (const item of items) {
        nameMap.set(item.id, item.name);
        defaultRoleById.set(
          item.id,
          item.defaultRoleFeeKind ?? "performer",
        );
      }
      setNames(nameMap);
      setLinesByPerformer((prev) => {
        const next = new Map(prev);
        for (const id of performerIds) {
          if (!next.has(id)) {
            next.set(id, [
              newLine(
                defaultLinePayload(
                  defaultRoleById.get(id) ?? "performer",
                  resolvedShowCount,
                  eventShows,
                ),
              ),
            ]);
          }
        }
        for (const id of [...next.keys()]) {
          if (!performerIds.includes(id)) next.delete(id);
        }
        return next;
      });
      setSelectedId((prev) =>
        prev && performerIds.includes(prev) ? prev : (performerIds[0] ?? null),
      );
    });

    return () => {
      cancelled = true;
    };
  }, [performerIds, eventKind]);

  const assignments = useMemo(() => {
    const showPickerEnabled = (eventShows?.length ?? 0) >= 2;
    const result: CreateAssignmentDTO[] = [];
    for (const id of performerIds) {
      for (const line of linesByPerformer.get(id) ?? []) {
        if (line.payload) {
          const { showCount, eventShowIds, ...rest } = line.payload;
          result.push({
            performerId: id,
            ...rest,
            ...(showPickerEnabled && eventShowIds && eventShowIds.length > 0
              ? { eventShowIds }
              : { showCount }),
          });
        }
      }
    }
    return result;
  }, [performerIds, linesByPerformer, eventShows]);

  useEffect(() => {
    onChange({ assignments, performerNames: names });
  }, [assignments, names, onChange]);

  const updateLine = (
    performerId: string,
    lineId: string,
    payload: AssignmentRulePayload | null,
  ) => {
    setLinesByPerformer((prev) => {
      const next = new Map(prev);
      const lines = next.get(performerId) ?? [];
      next.set(
        performerId,
        lines.map((l) => (l.id === lineId ? { ...l, payload } : l)),
      );
      return next;
    });
  };

  const addLine = (performerId: string) => {
    setLinesByPerformer((prev) => {
      const next = new Map(prev);
      const existing = next.get(performerId) ?? [];
      const fallbackRole =
        existing.find((l) => l.payload)?.payload?.roleFeeKind ?? "performer";
      next.set(performerId, [
        ...existing,
        newLine(defaultLinePayload(fallbackRole, resolvedShowCount, eventShows)),
      ]);
      return next;
    });
  };

  const removeLine = (performerId: string, lineId: string) => {
    setLinesByPerformer((prev) => {
      const next = new Map(prev);
      const lines = (next.get(performerId) ?? []).filter(
        (l) => l.id !== lineId,
      );
      next.set(performerId, lines.length > 0 ? lines : [newLine(defaultLinePayload("performer", resolvedShowCount, eventShows))]);
      return next;
    });
  };

  const copyToAll = (fromId: string) => {
    const source = (linesByPerformer.get(fromId) ?? [])
      .map((l) => l.payload)
      .filter((p): p is AssignmentRulePayload => p != null);
    if (source.length === 0) return;
    setLinesByPerformer((prev) => {
      const next = new Map(prev);
      for (const id of performerIds) {
        if (id === fromId) continue;
        next.set(
          id,
          source.map((p) => newLine(p)),
        );
      }
      return next;
    });
  };

  const existingKeys = useMemo(
    () =>
      new Set(
        existingAssignments.map(
          (row) => `${row.performerId}:${row.roleFeeKind}:${row.lineKey}`,
        ),
      ),
    [existingAssignments],
  );

  const countFor = (performerId: string) =>
    (linesByPerformer.get(performerId) ?? []).filter((l) => l.payload).length;

  const selectedLines = selectedId
    ? (linesByPerformer.get(selectedId) ?? [])
    : [];

  const handleQuickCreated = (performer: {
    id: string;
    name: string;
    defaultRoleFeeKind?: RoleFeeKind;
  }) => {
    setNames((prev) => {
      const next = new Map(prev);
      next.set(performer.id, performer.name);
      return next;
    });
    setLinesByPerformer((prev) => {
      const next = new Map(prev);
      if (!next.has(performer.id)) {
        next.set(performer.id, [
          newLine(
            defaultLinePayload(
              performer.defaultRoleFeeKind ?? "performer",
              resolvedShowCount,
              eventShows,
            ),
          ),
        ]);
      }
      return next;
    });
    setPerformerIds((prev) =>
      prev.includes(performer.id) ? prev : [...prev, performer.id],
    );
    setSelectedId(performer.id);
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[18rem_1fr] lg:items-start">
      <div className="space-y-3">
        <PerformerMultiSelect
          required
          values={performerIds}
          onValuesChange={setPerformerIds}
          lookupUrl={performerLookupUrl(eventKind)}
          queryKeyPrefix={["performance", "performers", "builder", eventKind]}
          disabled={submitting}
          hint="ຄົ້ນຫາ ແລະ ເລືອກຫຼາຍຄົນ"
        />

        {canCreatePerformer && !quickCreateOpen && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full gap-1.5"
            disabled={submitting}
            onClick={() => setQuickCreateOpen(true)}
          >
            <UserPlusIcon className="size-3.5" />
            ເພີ່ມນັກສະແດງດ່ວນ
          </Button>
        )}

        {canCreatePerformer && quickCreateOpen && (
          <QuickCreatePerformerPanel
            eventKind={eventKind}
            onCancel={() => setQuickCreateOpen(false)}
            onCreated={(performer) => {
              handleQuickCreated(performer);
              setQuickCreateOpen(false);
            }}
          />
        )}

        {performerIds.length > 0 && (
          <ul className="space-y-1.5">
            {performerIds.map((id) => {
              const isActive = id === selectedId;
              const count = countFor(id);
              return (
                <li key={id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(id)}
                    className={`flex w-full items-center justify-between gap-2 rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                      isActive
                        ? "border-primary bg-primary/5"
                        : "bg-background hover:bg-muted/40"
                    }`}
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <UserIcon className="size-3.5 shrink-0 text-muted-foreground" />
                      <span className="truncate">{names.get(id) ?? id}</span>
                    </span>
                    <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-muted-foreground text-xs">
                      {count}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="min-h-0">
        {!selectedId ? (
          <p className="rounded-lg border border-dashed bg-muted/10 px-4 py-10 text-center text-muted-foreground text-sm">
            ເລືອກນັກສະແດງຊ້າຍກ່ອນ — ແລ້ວກຳນົດຄ່າຕົວໃຫ້ແຕ່ລະຄົນ
          </p>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <p className="min-w-0 truncate font-medium text-sm">
                {names.get(selectedId) ?? selectedId}
                <span className="font-normal text-muted-foreground">
                  {" "}
                  · {countFor(selectedId)} ບົດບາດ
                </span>
              </p>
              {performerIds.length > 1 && countFor(selectedId) > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 shrink-0 gap-1"
                  disabled={submitting}
                  onClick={() => copyToAll(selectedId)}
                >
                  <CopyIcon className="size-3.5" />
                  ໃຊ້ກັບທຸກຄົນ
                </Button>
              )}
            </div>

            <div className="space-y-2">
              {selectedLines.map((line, index) => {
                const lineKey = line.payload
                  ? resolvePlannedLineKey(line.payload)
                  : null;
                const isDuplicate =
                  lineKey != null &&
                  line.payload != null &&
                  existingKeys.has(
                    `${selectedId}:${line.payload.roleFeeKind}:${lineKey}`,
                  );
                return (
                  <div
                    key={`${selectedId}:${line.id}`}
                    className="overflow-hidden rounded-md border bg-background"
                  >
                    <div className="flex items-center gap-2 border-b bg-muted/20 px-2.5 py-1">
                      <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-muted-foreground text-xs">
                        #{index + 1}
                      </span>
                      {isDuplicate && (
                        <span className="truncate text-amber-800 text-xs">
                          ມີແລ້ວ — ຈະຂ້າມ
                        </span>
                      )}
                      <span className="flex-1" />
                      {selectedLines.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="size-7 shrink-0 p-0 text-destructive hover:bg-destructive/10"
                          disabled={submitting}
                          onClick={() => removeLine(selectedId, line.id)}
                          aria-label="ລຶບບົດບາດ"
                        >
                          <TrashIcon className="size-3.5" />
                        </Button>
                      )}
                    </div>
                    <div className="px-2.5 py-2">
                      <AssignmentForm
                        key={line.id}
                        eventKind={eventKind}
                        derivedShowCount={resolvedShowCount}
                        eventShows={eventShows}
                        variant="bulk"
                        compact
                        hideActions
                        submitting={submitting}
                        initialValues={
                          line.payload
                            ? payloadToInitial(line.payload)
                            : {
                                showCount: resolvedShowCount,
                              }
                        }
                        onSubmit={() => {}}
                        onRulePayloadChange={(payload) =>
                          updateLine(selectedId, line.id, payload)
                        }
                      />
                    </div>
                  </div>
                );
              })}

              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1"
                disabled={submitting}
                onClick={() => addLine(selectedId)}
              >
                <PlusIcon className="size-3.5" />
                ເພີ່ມບົດບາດ
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
